<!-- eslint-disable perfectionist/sort-imports -->
<script setup lang="ts">
import { computed } from 'vue';

import ActionButton from '~/components/shared/ActionButton.vue';
import Pagination from '~/components/Pagination';

import type { Campaign } from '../types';

import {
  formatNumber,
  formatPct,
  isHighPct,
} from '../utils';
import CampaignStatusBadge from './CampaignStatusBadge.vue';

const props = defineProps<{
  currentPage: number;
  hasNext: boolean;
  isLoading: boolean;
  pageSize: number;
  rows: Campaign[];
  totalCount: number;
}>();

const emit = defineEmits<{
  'page-change': [page: number];
  'page-size-change': [pageSize: number];
  view: [campaign: Campaign];
}>();

const startRow = computed(() => (props.currentPage - 1) * props.pageSize + 1);
const endRow = computed(() => Math.min(props.currentPage * props.pageSize, props.totalCount));
</script>

<template>
  <div class="col-12">
    <div class="camp-card">
      <div class="camp-table-wrap">
        <table class="camp-table">
          <thead>
            <tr>
              <th>Campanha</th>
              <th>Incentivo</th>
              <th class="text-center">Parceiros</th>
              <th class="text-center">Elegíveis</th>
              <th class="text-center">Revalidados</th>
              <th class="text-center">% Revalidado</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>

          <template v-if="isLoading">
            <tbody>
              <tr v-for="i in 8" :key="i">
                <td v-for="j in 8" :key="j">
                  <div class="placeholder-glow">
                    <span class="placeholder col-12 placeholder-row" />
                  </div>
                </td>
              </tr>
            </tbody>
          </template>

          <tbody v-else>
            <tr v-if="rows.length === 0">
              <td colspan="8" class="text-center py-4" style="color: #6b7280;">
                Nenhuma campanha encontrada
              </td>
            </tr>

            <tr v-for="campaign in rows" v-else :key="campaign.id">
              <td>
                <RouterLink
                  class="cell-link"
                  :to="{ path: '/revalida-campaign-detail', query: { id: campaign.id } }"
                >
                  {{ campaign.name }}
                </RouterLink>
                <div class="cell-muted">{{ campaign.code }} · criada por {{ campaign.createdBy }}</div>
              </td>

              <td class="cell-strong">{{ campaign.incentiveType }}</td>

              <td class="text-center cell-strong">{{ formatNumber(campaign.partnersCount) }}</td>

              <td class="text-center">{{ formatNumber(campaign.eligibleCount) }}</td>

              <td class="text-center">{{ formatNumber(campaign.revalidatedCount) }}</td>

              <td class="text-center">
                <strong v-if="isHighPct(campaign.revalidatedPct)" style="color: #00b569;">
                  {{ formatPct(campaign.revalidatedPct) }}
                </strong>
                <span v-else style="color: #6b7280;">{{ formatPct(campaign.revalidatedPct) }}</span>
              </td>

              <td>
                <CampaignStatusBadge :status="campaign.status" />
              </td>

              <td>
                <ActionButton type="details" @click="emit('view', campaign)" />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="!isLoading" class="camp-pagination">
        <span>
          <template v-if="totalCount > 0">
            Mostrando {{ startRow }}–{{ endRow }} de {{ totalCount }} campanhas
          </template>
          <template v-else>
            Nenhuma campanha encontrada
          </template>
        </span>

        <Pagination
          v-if="totalCount > 0"
          class="camp-pagination-controls"
          :current-page="currentPage"
          :has-next="hasNext"
          :page-size="pageSize"
          @page-change="emit('page-change', $event)"
          @page-size-change="emit('page-size-change', $event)"
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.camp-card {
  background: #fff;
  border: 1px solid #e4e6eb;
  border-radius: 12px;
  overflow: hidden;
}

.camp-table-wrap {
  overflow-x: auto;
}

.camp-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  thead th {
    background: #fafafb;
    color: #6b7280;
    text-align: left;
    padding: 12px 16px;
    font-weight: 600;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-bottom: 1px solid #e4e6eb;
    white-space: nowrap;

    &.text-center {
      text-align: center;
    }
  }

  tbody td {
    padding: 14px 16px;
    border-bottom: 1px solid #e4e6eb;
    vertical-align: middle;
    color: #222222;

    &.text-center {
      text-align: center;
    }
  }

  tbody tr:hover td {
    background: #fafbfc;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }
}

.cell-link {
  color: #00b569;
  font-weight: 600;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

.cell-muted {
  color: #6b7280;
  font-size: 12px;
  margin-top: 2px;
}

.cell-strong {
  font-weight: 600;
  color: #222222;
}

.camp-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 20px;
  font-size: 12px;
  color: #6b7280;
  border-top: 1px solid #e4e6eb;
  flex-wrap: wrap;
}

.camp-pagination-controls {
  :deep(> .d-flex) {
    margin: 0 !important;
    padding-right: 0 !important;
    width: auto !important;
  }
}

.placeholder-row {
  display: inline-block;
  height: 16px;
  border-radius: 3px;
}
</style>
